import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Sparkles, ArrowRight, Mail, Lock, Eye, EyeOff, ArrowLeft, Key, Shield, AlertTriangle } from 'lucide-react';
import BrandHeader from '../components/BrandHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Recovery state
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryMethod, setRecoveryMethod] = useState('recovery_code');
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [securityQuestion, setSecurityQuestion] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [recoverySuccess, setRecoverySuccess] = useState('');
  const [newRecoveryCode, setNewRecoveryCode] = useState('');
  const [showEmergency, setShowEmergency] = useState(false);
  const [emergencyToken, setEmergencyToken] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/admin');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map(e => e.msg || JSON.stringify(e)).join(' '));
      } else {
        setError('Login failed. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSecurityQuestion = async (emailVal) => {
    try {
      const res = await axios.get(`${API_URL}/api/recovery/security-question/${encodeURIComponent(emailVal)}`);
      const qs = res.data.security_questions || [];
      setSecurityQuestion(qs.length > 0 ? qs.join(' / ') : '');
    } catch (e) {
      setSecurityQuestion('');
    }
  };

  const handleRecovery = async () => {
    setError('');
    setLoading(true);
    try {
      const payload = {
        email: recoveryEmail,
        method: recoveryMethod,
        new_password: newPassword
      };
      if (recoveryMethod === 'recovery_code') payload.recovery_code = recoveryCode;
      else payload.security_answer = securityAnswer;

      const res = await axios.post(`${API_URL}/api/recovery/reset-password`, payload);
      setRecoverySuccess(res.data.message);
      setNewRecoveryCode(res.data.new_recovery_code || '');
    } catch (err) {
      setError(err.response?.data?.detail || 'Recovery failed');
    } finally {
      setLoading(false);
    }
  };

  const handleEmergencyReset = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/emergency-reset`, { token: emergencyToken });
      setRecoverySuccess(res.data.message);
    } catch (err) {
      setError(err.response?.data?.detail || 'Emergency reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fdfdfc] flex flex-col">
      <BrandHeader backTo="/" backLabel="Back to home" />

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Admin Login Card */}
          {!showRecovery ? (
          <div className="card-olympiad p-8 mb-6 animate-fade-in-up stagger-1" data-testid="admin-login-card">
            <h2 className="text-xl font-bold text-[#0f172a] mb-6 text-center" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Admin Login
            </h2>

            {error && (
              <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 mb-6 text-red-600 text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#475569] mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748b]" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-olympiad pl-12"
                    placeholder="admin@olympiad.com"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#475569] mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748b]" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-olympiad pl-12 pr-12"
                    placeholder="Enter your password"
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#64748b] hover:text-[#0f172a]"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="login-submit-button"
              >
                {loading ? 'Signing in...' : 'Sign In'}
                {!loading && <ArrowRight className="w-5 h-5" />}
              </button>
            </form>
            <button
              onClick={() => { setShowRecovery(true); setError(''); setRecoverySuccess(''); setNewRecoveryCode(''); }}
              className="w-full text-center text-sm font-semibold text-[#2565eb] hover:underline mt-4"
              data-testid="forgot-password-link"
            >
              Forgot Password?
            </button>
          </div>
          ) : (
          /* ===== PASSWORD RECOVERY CARD ===== */
          <div className="card-olympiad p-8 mb-6 animate-fade-in-up" data-testid="recovery-card">
            <button
              onClick={() => { setShowRecovery(false); setShowEmergency(false); setError(''); setRecoverySuccess(''); }}
              className="flex items-center gap-1 text-sm font-semibold text-[#2565eb] hover:underline mb-4"
              data-testid="back-to-login"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Login
            </button>

            {recoverySuccess ? (
              <div data-testid="recovery-success">
                <div className="bg-[#dcfce7] border-2 border-[#86efac] rounded-2xl p-4 mb-4 text-[#16a34a] text-sm font-medium">
                  {recoverySuccess}
                </div>
                {newRecoveryCode && (
                  <div className="bg-[#eff6ff] border-2 border-[#2565eb] rounded-2xl p-5 mb-4">
                    <div className="flex items-start gap-2 mb-2">
                      <AlertTriangle className="w-5 h-5 text-[#d97706] flex-shrink-0" />
                      <p className="text-sm font-bold text-[#0f172a]">Save your new recovery code now:</p>
                    </div>
                    <code className="block bg-white px-4 py-3 rounded-xl text-lg font-mono font-bold text-[#2565eb] border-2 border-[#bfdbfe] tracking-wider text-center" data-testid="new-recovery-code">
                      {newRecoveryCode}
                    </code>
                  </div>
                )}
                <button
                  onClick={() => { setShowRecovery(false); setRecoverySuccess(''); setNewRecoveryCode(''); }}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                >
                  Return to Login <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            ) : showEmergency ? (
              /* Emergency Reset */
              <div data-testid="emergency-reset-form">
                <h2 className="text-xl font-bold text-[#0f172a] mb-2 text-center" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  Emergency Reset
                </h2>
                <p className="text-sm text-[#64748b] text-center mb-6">Enter the master reset token from your server environment.</p>

                {error && (
                  <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 mb-4 text-red-600 text-sm">{error}</div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#475569] mb-2">Emergency Token</label>
                    <div className="relative">
                      <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748b]" />
                      <input
                        type="text"
                        value={emergencyToken}
                        onChange={(e) => setEmergencyToken(e.target.value)}
                        className="input-olympiad pl-12 font-mono"
                        placeholder="BGYM-EMERGENCY-..."
                        data-testid="emergency-token-input"
                      />
                    </div>
                  </div>
                  <button
                    onClick={handleEmergencyReset}
                    disabled={loading || !emergencyToken.trim()}
                    className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="emergency-reset-button"
                  >
                    {loading ? 'Resetting...' : 'Reset to Default Password'}
                  </button>
                </div>
              </div>
            ) : (
              /* Recovery Code / Security Question */
              <div>
                <h2 className="text-xl font-bold text-[#0f172a] mb-2 text-center" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  Reset Password
                </h2>
                <p className="text-sm text-[#64748b] text-center mb-6">Use your recovery code or security question to reset your password.</p>

                {error && (
                  <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 mb-4 text-red-600 text-sm">{error}</div>
                )}

                {/* Method toggle */}
                <div className="flex gap-2 mb-5">
                  <button
                    onClick={() => setRecoveryMethod('recovery_code')}
                    className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                      recoveryMethod === 'recovery_code' ? 'bg-[#2565eb] text-white' : 'bg-[#f1f5f9] text-[#475569] hover:bg-[#e2e8f0]'
                    }`}
                    data-testid="method-recovery-code"
                  >
                    <Key className="w-4 h-4" /> Recovery Code
                  </button>
                  <button
                    onClick={() => setRecoveryMethod('security_question')}
                    className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                      recoveryMethod === 'security_question' ? 'bg-[#2565eb] text-white' : 'bg-[#f1f5f9] text-[#475569] hover:bg-[#e2e8f0]'
                    }`}
                    data-testid="method-security-question"
                  >
                    <Shield className="w-4 h-4" /> Security Question
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#475569] mb-2">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748b]" />
                      <input
                        type="email"
                        value={recoveryEmail}
                        onChange={(e) => { setRecoveryEmail(e.target.value); if (recoveryMethod === 'security_question') fetchSecurityQuestion(e.target.value); }}
                        className="input-olympiad pl-12"
                        placeholder="admin@olympiad.com"
                        data-testid="recovery-email-input"
                      />
                    </div>
                  </div>

                  {recoveryMethod === 'recovery_code' ? (
                    <div>
                      <label className="block text-sm font-semibold text-[#475569] mb-2">Recovery Code</label>
                      <div className="relative">
                        <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748b]" />
                        <input
                          type="text"
                          value={recoveryCode}
                          onChange={(e) => setRecoveryCode(e.target.value)}
                          className="input-olympiad pl-12 font-mono"
                          placeholder="REC-XXXX-XXXX"
                          data-testid="recovery-code-input"
                        />
                      </div>
                    </div>
                  ) : (
                    <div>
                      {securityQuestion && (
                        <p className="text-sm text-[#475569] mb-2"><span className="font-semibold">Answer any one:</span> {securityQuestion}</p>
                      )}
                      <div className="relative">
                        <Shield className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748b]" />
                        <input
                          type="text"
                          value={securityAnswer}
                          onChange={(e) => setSecurityAnswer(e.target.value)}
                          className="input-olympiad pl-12"
                          placeholder="Your answer"
                          data-testid="security-answer-recovery-input"
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-semibold text-[#475569] mb-2">New Password</label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748b]" />
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="input-olympiad pl-12"
                        placeholder="At least 8 characters"
                        data-testid="new-password-recovery-input"
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleRecovery}
                    disabled={loading || !recoveryEmail || !newPassword}
                    className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="reset-password-button"
                  >
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </button>
                </div>

                <button
                  onClick={() => { setShowEmergency(true); setError(''); }}
                  className="w-full text-center text-xs font-medium text-[#94a3b8] hover:text-[#64748b] mt-4"
                  data-testid="emergency-reset-link"
                >
                  Lost everything? Emergency master reset
                </button>
              </div>
            )}
          </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="p-6 text-center text-sm text-[#64748b]">
        <p>&copy; {new Date().getFullYear()} Brain Gym Olympiad. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default LoginPage;
